import React, { useRef, useState, useEffect } from "react";
import { string, object } from "prop-types";
import uniqueid from "lodash.uniqueid";
import cx from "classnames";

import useClickAway from "../../common/useClickAway.js";

import Icon from "../Icon/Icon.jsx";
import TextInput from "../Form/TextInput.jsx";

const propTypes = {
  label: string,
  textInput: object,
  searchUrl: string,
  buttonText: string,
  placeholder: string,
};

// Component is used as both React and Vanilla JS so the `js-classes` are required
const SearchBox = (props) => {
  const { label, textInput, searchUrl, buttonText = "Search" } = props;

  const searchId = uniqueid("site-search");
  const placeholder =
    (textInput && textInput.placholder) || props.placeholder || "";

  const [isOpen, setIsOpen] = useState(false);

  const inputRef = useRef();
  const buttonRef = useRef();
  const containerRef = useClickAway({
    isOpen: isOpen,
    setOpen: setIsOpen,
  });

  // initializing timeout to be the correct type to avoid errors.
  let timeout = setTimeout(() => {}, 1);

  const handleToggleClick = () => {
    clearTimeout(timeout);
    if (!isOpen) {
      timeout = setTimeout(() => {
        inputRef.current.focus();
      }, 800);
    }

    setIsOpen(!isOpen);
  };

  const handleButtonKeyDown = (e) => {
    if (e.key === "Tab" && !e.shiftKey) {
      e.preventDefault();
      setIsOpen(false);
      buttonRef.current.focus();
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Tab" && e.shiftKey) {
      e.preventDefault();
      setIsOpen(false);
      buttonRef.current.focus();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Esc" || e.key === "Escape") {
        setIsOpen(false);
        buttonRef.current.focus();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    } else {
      document.removeEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div
      className={cx("search-box js-search-box", {
        "search-box--open": isOpen,
      })}
      ref={containerRef}
    >
      <div className="search-box__controls">
        <button
          type="button"
          className="search-box__toggle btn js-search-box-toggle-button"
          aria-label="Show search input"
          aria-controls={searchId}
          onClick={handleToggleClick}
          ref={buttonRef}
          aria-expanded={isOpen}
          data-gtm-element="global nav search toggle"
        >
          <div className="search-box__label">{label || "Search"}</div>
          <Icon className="icon--24x24" iconName="search" />
          <Icon className="icon--24x24" iconName="close" />
        </button>
      </div>
      <div className="search-box__dropdown js-search-box-content" id={searchId}>
        <form
          action={searchUrl}
          method="GET"
          className="sc-form js-search-box-form search-box__form container"
        >
          <TextInput
            {...textInput}
            type="search"
            focusRef={inputRef}
            keyDownHandler={handleInputKeyDown}
            placeholder={placeholder}
            name="q"
            className="sc-form-item__field--search"
          />
          <button
            type="submit"
            className="search-box__search-button btn btn--primary"
            onKeyDown={handleButtonKeyDown}
            data-gtm-element="global nav search button"
            data-gtm-link={searchUrl}
          >
            <span>{buttonText}</span>
            <Icon iconName="search" className="icon--32x32" />
          </button>
        </form>
      </div>
    </div>
  );
};

SearchBox.propTypes = propTypes;
export default SearchBox;
